import { useMemo, useState } from "react";
import { useToast } from "@repo/ui";
import { getErrorMessage } from "~/hooks/helpers";
import type { StatusContainerId } from "~/pages/administration/lists/lists-dialog/status/dnd";
import { StatusTransferList } from "~/pages/administration/lists/lists-dialog/status/status-transfer-list";
import { useLinkActionStatus } from "~/pages/administration/workflows-page/hooks/apis/use-link-action-mutations";
import { useUnlinkActionStatus } from "~/pages/administration/workflows-page/hooks/apis/use-unlink-action-mutations";
import { useWorkflowCreationContext } from "~/pages/administration/workflows-page/workflow-creation-context-provider";
import type { CreateLocalActionRequest } from "~/pages/administration/workflows-page/workflows-dialog/views/action-view/hooks/use-action-view";
import type { ListStatuses } from "~/types/api";
import { WorkflowsDialogFooter } from "../workflows-dialog-footer";

type LinkableStatus = { code: number; name: string };

type Props = {
  action: CreateLocalActionRequest;
  actionIndex: number;
  statuses: LinkableStatus[];
  isEditMode?: boolean;
  isPersisted?: boolean;
  workflowId?: number;
  onRefetch?: () => Promise<unknown>;
  onClose: () => void;
};

const toListStatuses = (statuses: LinkableStatus[]): ListStatuses[] =>
  statuses.map((s) => ({ code: s.code, name: s.name, sortOrder: 0 }));

export const ActionStatusLinkDialog = ({
  action,
  actionIndex,
  statuses,
  isEditMode,
  isPersisted,
  workflowId,
  onRefetch,
  onClose,
}: Props) => {
  const {
    state: { actions: allActions },
    updateState,
  } = useWorkflowCreationContext();
  const { showToast } = useToast();
  const { linkStatus } = useLinkActionStatus();
  const { unLinkStatus } = useUnlinkActionStatus();

  const initialLinked = useMemo(() => new Set(action.statusCodes ?? []), [action.statusCodes]);

  const [linkedCodes, setLinkedCodes] = useState<Set<number>>(initialLinked);
  const [availableSelection, setAvailableSelection] = useState<number[]>([]);
  const [linkedSelection, setLinkedSelection] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const allStatuses = useMemo(() => toListStatuses(statuses), [statuses]);

  const available = useMemo(
    () => allStatuses.filter((s) => !linkedCodes.has(s.code)),
    [allStatuses, linkedCodes]
  );
  const linked = useMemo(
    () => allStatuses.filter((s) => linkedCodes.has(s.code)),
    [allStatuses, linkedCodes]
  );

  const handleMove = (codes: number[], to: StatusContainerId) =>
    setLinkedCodes((prev) =>
      to === "selected"
        ? new Set([...prev, ...codes])
        : new Set([...prev].filter((c) => !codes.includes(c)))
    );

  const hasChanges = useMemo(() => {
    if (linkedCodes.size !== initialLinked.size) return true;
    for (const code of linkedCodes) {
      if (!initialLinked.has(code)) return true;
    }
    return false;
  }, [linkedCodes, initialLinked]);

  const handleSave = async () => {
    const nextCodes = [...linkedCodes];

    const updatedActions = allActions.map((a) =>
      a.id === action.id ? { ...a, statusCodes: nextCodes } : a
    );
    updateState("actions", updatedActions);

    if (isEditMode && isPersisted && workflowId && action.id != null) {
      const toLink = nextCodes.filter((code) => !initialLinked.has(code));
      const toUnlink = [...initialLinked].filter((code) => !linkedCodes.has(code));

      if (toLink.length > 0 || toUnlink.length > 0) {
        setIsSaving(true);
        try {
          await Promise.all([
            ...toLink.map((code) => linkStatus(workflowId, action.id as number, code, actionIndex)),
            ...toUnlink.map((code) => unLinkStatus(workflowId, action.id as number, code)),
          ]);
          await onRefetch?.();
        } catch (e) {
          const message = getErrorMessage(e, "Failed to update linked statuses");
          showToast("error", message, "Status update failed.");
          updateState(
            "actions",
            allActions.map((a) =>
              a.id === action.id ? { ...a, statusCodes: [...initialLinked] } : a
            )
          );
          return;
        } finally {
          setIsSaving(false);
        }

        const many = toLink.length + toUnlink.length > 1;
        showToast(
          "success",
          `${many ? "Statuses" : "Status"} toggled successfully.`,
          "Status update completed."
        );
      }
    }

    onClose();
  };

  return (
    <div className="flex flex-col gap-4">
      <StatusTransferList
        availableStatuses={available}
        chosenStatuses={linked}
        selectedAvailableItems={availableSelection}
        selectedChosenItems={linkedSelection}
        setSelectedAvailableItems={setAvailableSelection}
        setSelectedChosenItems={setLinkedSelection}
        onMove={handleMove}
        availableTitle="Available Statuses"
        chosenTitle="Linked Statuses"
        availableEmptyPlaceholder="All statuses linked"
        chosenEmptyPlaceholder="No statuses linked"
      />

      <WorkflowsDialogFooter
        onCancel={onClose}
        onSave={handleSave}
        loading={isSaving}
        disableSave={!hasChanges || isSaving}
      />
    </div>
  );
};
